import type { ReactNode } from 'react';
import PhoneInput, { formatPhoneNumberIntl, getCountries, isValidPhoneNumber, parsePhoneNumber, type Country } from 'react-phone-number-input';
import arLabels from 'react-phone-number-input/locale/ar.json';
import enLabels from 'react-phone-number-input/locale/en.json';
import 'react-phone-number-input/style.css';
import './phone.css';

/**
 * A phone number field the way people expect one: the country (flag, name,
 * dialling code) first, then the number, typed as they would say it.
 *
 * It opens on the country the server guessed for this visitor, keeps the
 * value in E.164 (+966501234567) whatever is typed or pasted, and is the same
 * field on the customer's page and in the agent's link dialog. The number is
 * always written left to right, also in an Arabic page.
 */

export type PhoneLocale = 'en' | 'ar';

/** Flags are files on this site (see flagAssets in vite.config.ts), one per country. */
declare const __FLAGS_BASE__: string;
const FLAG_URL = `${__FLAGS_BASE__}{XX}.svg`;

const KNOWN = new Set<string>(getCountries());

export function asCountry(code: string | null | undefined): Country | undefined {
  const c = (code ?? '').trim().toUpperCase();
  return KNOWN.has(c) ? (c as Country) : undefined;
}

function labelsFor(locale: PhoneLocale): Record<string, string> {
  return (locale === 'ar' ? arLabels : enLabels) as Record<string, string>;
}

export interface PhoneFieldProps {
  id: string;
  /** E.164, or '' while empty. */
  value: string;
  onChange: (e164: string) => void;
  defaultCountry?: string | null;
  locale: PhoneLocale;
  /** Accessible names: the number input and the country list. */
  label: string;
  countryLabel: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
}

export function PhoneField(props: PhoneFieldProps) {
  return (
    <div className={`pf${props.invalid ? ' pf--invalid' : ''}${props.disabled ? ' pf--disabled' : ''}`} dir="ltr">
      <PhoneInput
        id={props.id}
        international
        withCountryCallingCode
        defaultCountry={asCountry(props.defaultCountry)}
        value={props.value || undefined}
        onChange={(v) => props.onChange(v ?? '')}
        flagUrl={FLAG_URL}
        labels={labelsFor(props.locale)}
        disabled={props.disabled}
        countrySelectProps={{ 'aria-label': props.countryLabel }}
        numberInputProps={{
          'aria-label': props.label,
          required: props.required,
          autoFocus: props.autoFocus,
          autoComplete: 'tel',
          inputMode: 'tel',
          'aria-invalid': props.invalid || undefined,
        }}
      />
    </div>
  );
}

export interface PhoneDescription {
  valid: boolean;
  /** "+966 50 123 4567" - how the number will be shown and dialled. */
  international: string | null;
  country: Country | null;
  countryName: string | null;
}

/** What a value means: complete or not, written out, and whose country. */
export function describePhone(value: string, locale: PhoneLocale): PhoneDescription {
  if (!value) return { valid: false, international: null, country: null, countryName: null };
  let country: Country | null = null;
  try {
    country = parsePhoneNumber(value)?.country ?? null;
  } catch {
    country = null;
  }
  return {
    valid: isValidPhoneNumber(value),
    international: formatPhoneNumberIntl(value) || value,
    country,
    countryName: country ? (labelsFor(locale)[country] ?? country) : null,
  };
}

/** A stored number written out for people: "+46 70 123 45 67". */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  return formatPhoneNumberIntl(e164) || e164;
}

/**
 * The line under the field: the number as it will be kept, with its country,
 * or what is missing. `texts` comes from the page's own dictionary.
 */
export function PhonePreview({
  value,
  locale,
  texts,
  extra,
}: {
  value: string;
  locale: PhoneLocale;
  texts: { willUse: string; incomplete: string; empty?: string };
  extra?: ReactNode;
}) {
  const d = describePhone(value, locale);
  if (!value) return texts.empty ? <p className="pf-preview pf-preview--empty">{texts.empty}</p> : null;
  return (
    <p className={`pf-preview${d.valid ? ' pf-preview--ok' : ' pf-preview--bad'}`} role="status" aria-live="polite">
      {d.valid ? (
        <>
          <span className="pf-preview__label">{texts.willUse}</span>{' '}
          <bdi className="pf-preview__number" dir="ltr">
            {d.international}
          </bdi>
          {d.countryName ? <span className="pf-preview__country"> · {d.countryName}</span> : null}
          {extra}
        </>
      ) : (
        <>{texts.incomplete.replace('{country}', d.countryName ?? '')}</>
      )}
    </p>
  );
}
